import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FormlyErrorComponent } from '../formly-error.component';
import { applyParsedValue, formatCurrencyAmount } from '../../utils/currency.utils';
import { FieldLabelRowComponent } from './field-label-row.component';

@Component({
  selector: 'formly-field-currency',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    FormlyModule,
    FormlyDataCyDirective,
    FormlyErrorComponent,
    FieldLabelRowComponent,
  ],
  template: `
    <mat-form-field class="dd-compact-field w-full">
      @if (props.label) {
        <mat-label>
          <app-field-label-row [label]="props.label" [required]="props.required"></app-field-label-row>
        </mat-label>
      }
      <span class="text-md text-gray-600 pl-0 pr-1 inline-block" matPrefix>
        @if (props.addonLeft?.icon) {
          <mat-icon>{{ props.addonLeft.icon }}</mat-icon>
        } @else if (props.addonLeft?.text) {
          <span>{{ props.addonLeft.text }}</span>
        } @else {
          €
        }
      </span>
      <input
        #inputEl
        matInput
        [id]="field.id"
        type="text"
        inputmode="decimal"
        [value]="displayValue()"
        [disabled]="isDisabled()"
        [required]="props.required"
        [formlyAttributes]="field"
        [formlyDataCy]="field"
        [placeholder]="props.placeholder || ''"
        (focus)="onFocus(inputEl)"
        (blur)="onBlur(inputEl)" />
      @if (props.addonRight) {
        <span class="text-md text-gray-600 px-2 inline-block" matSuffix>
          @if (props.addonRight.icon) {
            <mat-icon>{{ props.addonRight.icon }}</mat-icon>
          }
          @if (props.addonRight.text) {
            <span>{{ props.addonRight.text }}</span>
          }
        </span>
      }
      @if (props.description) {
        <mat-hint>{{ props.description }}</mat-hint>
      }
    </mat-form-field>
    <app-formly-error [field]="field" />
  `,
})
export class FormlyFieldCurrencyComponent extends FieldType<FieldTypeConfig> implements OnInit {
  readonly #isFocused = signal(false);
  readonly #destroyRef = inject(DestroyRef);

  // Bridge formControl.value into a signal so `displayValue` re-evaluates on
  // external patches. Subscription lives in ngOnInit because Formly assigns
  // `this.formControl` after construction.
  readonly #formValue = signal<number | null>(null);

  // The input binds [value], not [formControl], so it carries no NgControl — and MatInput only
  // mirrors a control's disabled state when an ngControl is present (input.mjs ngDoCheck). Its
  // host binding `[disabled]="disabled && !disabledInteractive"` otherwise pins the property to
  // false on every check, leaving a disabled control fully editable. Hand the state over
  // explicitly, bridged from statusChanges the same way #formValue bridges valueChanges.
  readonly #disabled = signal(false);
  readonly isDisabled = this.#disabled.asReadonly();

  // - During focus: raw editable nl-NL number (1250,50) — selected on focus
  //   for instant-replace UX (Stripe/Xero/QuickBooks pattern)
  // - Otherwise: formatted via Angular's native formatCurrency, same engine
  //   as {{ x | currency:'EUR' }} pipe used in unplanned-shift.ts:146
  readonly displayValue = computed(() => {
    const v = this.#formValue();
    if (v == null) return '';
    if (this.#isFocused()) return String(v).replace('.', ',');
    // Symbol-less — matPrefix in the template already renders the €. Shared with the
    // table's price column so form and list show the same amount.
    return formatCurrencyAmount(v);
  });

  ngOnInit(): void {
    this.#formValue.set(this.formControl.value as number | null);

    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(v => this.#formValue.set(v as number | null));

    this.#disabled.set(this.formControl.disabled);
    // enable()/disable() run updateValueAndValidity, so the status stream is the reliable signal.
    this.formControl.statusChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#disabled.set(this.formControl.disabled));

    if (this.props.required && this.formControl.value == null) {
      this.formControl.markAsTouched();
    }
  }

  onFocus(input: HTMLInputElement): void {
    this.#isFocused.set(true);
    // Select-all so user can immediately type-to-replace (industry default)
    // Defer to next tick so [value] binding has applied raw value first.
    queueMicrotask(() => input.select());
  }

  onBlur(input: HTMLInputElement): void {
    applyParsedValue(this.formControl, input.value);

    // Apply min/max constraints if configured (mirrors number.component behavior).
    // setValue inside applyParsedValue already triggered updateValueAndValidity,
    // so any FormControl-attached min/max validators have run. These props.min/max
    // checks layer prop-driven constraints on top, matching the sibling component.
    const value = this.formControl.value;
    if (typeof value === 'number') {
      if (this.props.min !== undefined && value < this.props.min) {
        this.formControl.setErrors({ ...this.formControl.errors, min: { min: this.props.min, actual: value } });
      }
      if (this.props.max !== undefined && value > this.props.max) {
        this.formControl.setErrors({ ...this.formControl.errors, max: { max: this.props.max, actual: value } });
      }
    }

    this.#isFocused.set(false);
  }

}
